import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import useApi, { doFetch, usePostCache } from "storybook-dashboard/utils/fetching";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";
import DataTable from "react-data-table-component";

import Im from "immutable";
import { formEventToData } from "../utils";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import CustomDropdown from "./dropdown";
import { unitChoices } from "AppSrc/project/metrics/forms";
import { Editor as TinyMCE } from "@tinymce/tinymce-react";

const MODAL_ID = "CommonBaseMetricModal001";

// Column definitions
const getConversionFactorColumns = () => [
  {
    name: "ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
  },
  {
    name: "To Unit",
    selector: (row) => row.get("toUnit"),
    sortable: true,
  },
  {
    name: "Factor",
    selector: (row) => row.get("factor"),
    sortable: true,
  },
  {
    name: "isGlobal?",
    selector: (row) => (row.get("is_global") ? "global" : "local"),
    sortable: true,
  },
];

const getBaseMetricRefsColumns = () => [
  {
    name: "Reporting Package / Project",
    cell: (row) => {
      const name = row.get("name");
      const projectId = row.get("project").substring(0, 8);
      const wpackId = row.get("uid").substring(0, 8);
      const url = name === "master" ? `/project/${projectId}` : `/project/${projectId}/wpack/${wpackId}/report`;
      const displayName = name === "master" ? `Project: ${projectId}` : name;

      return (
        <a
          href={url}
          onClick={(e) => {
            e.preventDefault();
            location.href = url;
          }}
        >
          {displayName}
        </a>
      );
    },
    sortable: true,
    grow: 2,
  },
  {
    name: "Project ID",
    selector: (row) => row.get("project").substring(0, 8),
    sortable: true,
  },
  {
    name: "Reporting Package ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
  },
];

const getBaseMetricAdminColumns = (categoriesSorted) => [
  {
    name: "ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
    width: "100px",
  },
  {
    name: "Parent ID",
    selector: (row) => {
      const parentId = row.get("parentmetric");
      return parentId ? parentId.substring(0, 8) : "";
    },
    sortable: true,
    width: "100px",
  },
  // {
  //   name: "Conv. Base",
  //   selector: (row) => (row.get("conversion_base") || "").substring(0, 8),
  //   sortable: true,
  //   width: "110px",
  // },
  {
    name: "Name",
    selector: (row) => row.get("name"),
    sortable: true,
    grow: 2,
  },
  {
    name: "Description",
    cell: (row) => (
      <div style={{ padding: "8px 0" }} dangerouslySetInnerHTML={{ __html: row.get("description") || "" }} />
    ),
    grow: 3,
    wrap: true,
  },
  {
    name: "Category",
    selector: (row) => row.get("category") || "",
    sortable: true,
    width: "120px",
  },
  {
    name: "Unit",
    selector: (row) => row.get("unit") || "",
    sortable: true,
    width: "100px",
  },
  {
    name: "Actions",
    cell: (row) => <ActionsCell row={row} categories={categoriesSorted} />,
    width: "100px",
    right: true,
    allowOverflow: true,
  },
];

function BaseMetricConversionFactors({ show, baseMetric }) {
  const { data } = usePostCache("/api/tenant/admin/dispatch/", {
    type: "METRIC_CONVERSION_FACTORS",
    payload: {
      basemetric_id: baseMetric.get("uid"),
    },
  });

  const columns = getConversionFactorColumns();

  if (!show || !data) {
    return null;
  }

  const conversionFactorsList = data.toList ? data.toList() : data;

  return (
    <DataTable
      columns={columns}
      data={conversionFactorsList.toArray()}
      noHeader={true}
      dense={true}
      striped={true}
      highlightOnHover={true}
      noDataComponent={<p>No conversion factors found for this base metric.</p>}
    />
  );
}

function BaseMetricRefs({ show, baseMetric }) {
  const { data } = usePostCache("/api/tenant/admin/dispatch/", {
    type: "METRIC_REFS",
    payload: {
      basemetric_id: baseMetric.get("uid"),
    },
  });

  const columns = getBaseMetricRefsColumns();

  if (!show) {
    return null;
  }

  const refs = (data || Im.List()).filter((i) => i.get("name") !== "master").sortBy((i) => i.get("project"));

  if (refs.isEmpty()) {
    return <p className="text-muted">No reporting packages found using this metric.</p>;
  }

  return <DataTable columns={columns} data={refs.toArray()} dense highlightOnHover striped noHeader />;
}

const mergeBaseMetrics = ({ e, baseMetricId, onSuccess }) => {
  const { from_metric_id } = formEventToData(e);

  doFetch("/api/tenant/admin/dispatch/", "POST", {
    type: "MERGE_BASEMETRICS",
    payload: {
      from_metric_id,
      to_metric_id: baseMetricId,
    },
  })
    .then((response) => response.json())
    .then((data) => onSuccess(data));
};

function MergeBaseMetricModal({ baseMetricId }) {
  const { trigger } = useApi("/api/tenant/admin/basemetric/");
  const { hideModal } = useModal();

  const onSuccess = (data) => {
    alert(JSON.stringify(data, null, 2));
    trigger();
    hideModal();
  };

  return (
    <Modal title={`Merge base metric to ${baseMetricId.substring(0, 8)}`}>
      <form onSubmit={(e) => mergeBaseMetrics({ e, baseMetricId, onSuccess })}>
        <div className="form-group">
          <label htmlFor="from_metric_id">From Base Metric ID</label>
          <input type="text" className="form-control" id="from_metric_id" name="from_metric_id" required />
        </div>
        <button type="submit" className="btn btn-primary">
          Merge
        </button>
      </form>
    </Modal>
  );
}

// Modal for showing conversion factors
function ConversionFactorsModal({ baseMetric }) {
  return (
    <Modal title={`Conversion Factors for ${baseMetric.get("name")}`} size="lg">
      <BaseMetricConversionFactors baseMetric={baseMetric} show={true} />
    </Modal>
  );
}

// Modal for showing reporting packages
function ReportingPackagesModal({ baseMetric }) {
  return (
    <Modal title={`Reporting Packages using ${baseMetric.get("name")}`} size="lg">
      <BaseMetricRefs baseMetric={baseMetric} show={true} />
    </Modal>
  );
}

// Actions cell component
function ActionsCell({ row, categories }) {
  const { setModal } = useModal();

  const dropDownItems = [
    {
      name: "Edit",
      onClick: () => {
        setModal(<BaseMetricFormModal baseMetric={row} categories={categories} />);
      },
    },
    {
      name: "Merge",
      onClick: () => {
        setModal(<MergeBaseMetricModal baseMetricId={row.get("uid")} />);
      },
    },
    {}, // Divider
    {
      name: "Show Conversion Factors",
      onClick: () => {
        setModal(<ConversionFactorsModal baseMetric={row} />);
      },
    },
    {
      name: "Show Reporting Packages",
      onClick: () => {
        setModal(<ReportingPackagesModal baseMetric={row} />);
      },
    },
  ];

  return <CustomDropdown items={dropDownItems} />;
}

export default function BaseMetricAdmin() {
  const { _companyId: companyId } = useParams();
  const { data: basemetrics } = useApi("/api/tenant/admin/basemetric/");
  const { data: categories } = useApi("/api/tenant/admin/category/");
  const [searchTerm, setSearchTerm] = useState("");

  const categoriesSorted = useMemo(() => {
    return (categories || Im.List())?.sortBy((category) => category.get("name"));
  }, [categories]);

  const columns = getBaseMetricAdminColumns(categoriesSorted);

  let basemetricsList = basemetrics || Im.List();

  // Filter basemetrics based on search term
  const filteredBasemetrics = useMemo(() => {
    if (!searchTerm.trim()) {
      return basemetricsList;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return basemetricsList.filter((metric) => {
      const id = metric.get("uid") || "";
      const name = metric.get("name") || "";

      return id.toLowerCase().includes(lowerSearchTerm) || name.toLowerCase().includes(lowerSearchTerm);
    });
  }, [basemetricsList, searchTerm]);

  let companyStr = companyId ? ` for company account ${companyId}` : null;

  return (
    <>
      <h3>BaseMetrics in Tenancy</h3>
      <p>This gives a list of all BaseMetrics within this tenancy {companyStr}</p>

      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Search by ID or Name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: "400px" }}
        />
      </div>

      <div>
        <DataTable
          columns={columns}
          data={filteredBasemetrics.toArray()}
          pagination
          paginationPerPage={20}
          paginationRowsPerPageOptions={[10, 20, 50, 100]}
          highlightOnHover={true}
          striped={true}
          defaultSortFieldId={4} // Sort by Name column by default
        />
      </div>
    </>
  );
}

const editBaseMetric = ({ e, baseMetric, data }) => {
  const baseMetricId = baseMetric.get("uid");
  return doFetch(`/api/tenant/admin/basemetric/${baseMetricId}/`, "PATCH", data);
};

// Function to add a base metric
export const addBaseMetric = ({ e, data }) => {
  return doFetch("/api/tenant/admin/basemetric/", "POST", data);
};

const tinyMCEConfig = {
  menubar: false,
  statusbar: false,
  content_css: "/static/bootstrap/css/bootstrap.css",
  plugins: "autolink link image lists print preview",
  block_formats: "Paragraph=p;Header 3=h3;Header 4=h4;Header 5=h5;",
  toolbar:
    "bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | sup sub | formatselect | removeformat",
};

export function BaseMetricForm({ baseMetric, onSuccess }) {
  const { data: categories } = useApi("/api/tenant/admin/category/");
  const [description, setDescription] = useState(baseMetric?.get("description") || "");

  const onSubmit = (e) => {
    // Add the TinyMCE description content to the form data
    const data = { ...formEventToData(e), description };
    const handler = baseMetric ? editBaseMetric : addBaseMetric;
    console.log("Submitting BaseMetricFormModal", baseMetric, data, handler);
    handler({ baseMetric, data }).then((response) => {
      if (response.ok) {
        onSuccess?.(response);
      }
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="form-group">
        <label htmlFor="name">Name</label>
        <input
          type="text"
          className="form-control"
          id="name"
          defaultValue={baseMetric?.get("name") || ""}
          name="name"
          required
        />
      </div>
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label htmlFor="unit">Unit</label>
            <select
              className="form-control"
              id="unit"
              name="unit"
              defaultValue={baseMetric?.get("unit") || ""}
              required
            >
              <option value="">Select a unit...</option>
              {unitChoices.map((unit, index) => (
                <option key={index} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              className="form-control"
              id="category"
              name="category"
              defaultValue={baseMetric?.get("category") || ""}
            >
              <option value="">Select a category...</option>
              {categories?.map((category) => (
                <option key={category.get("uid")} value={category.get("name")}>
                  {category.get("name")}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="description">Description</label>
        <TinyMCE
          name="description"
          initialValue={baseMetric?.get("description") || ""}
          init={tinyMCEConfig}
          onEditorChange={(content) => setDescription(content)}
        />
      </div>
      <button type="submit" className="btn btn-primary">
        {baseMetric ? "Update Base Metric" : "Create Base Metric"}
      </button>
    </form>
  );
}

// Modal component for getting or creating a base metric
export function BaseMetricFormModal({ baseMetric, setMetricId }) {
  const { trigger } = useApi("/api/tenant/admin/basemetric/");
  const [description, setDescription] = useState(baseMetric?.get("description") || "");

  const { hideModal } = useModal();

  const onSubmit = (e) => {
    // Add the TinyMCE description content to the form data
    const data = { ...formEventToData(e), description };
    const handler = baseMetric ? editBaseMetric : addBaseMetric;
    console.log("Submitting BaseMetricFormModal", baseMetric, data, handler);
    handler({ baseMetric, data }).then((response) => {
      if (response.ok) {
        trigger();
        hideModal();
        response.json().then((json) => {
          setMetricId?.(json.uid);
        });
      }
    });
  };

  const onSuccess = (response) => {
    trigger();
    hideModal();
  };

  return (
    <Modal title={baseMetric ? "Edit Base Metric" : "Get or Create Base Metric"}>
      <BaseMetricForm baseMetric={baseMetric} onSuccess={onSuccess} />
    </Modal>
  );
}
